import React, { memo, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Menu, Skeleton, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import PageSortPanel from '@components/PageSortPanel';
import LottieComponent from '@components/Lottie';
import {
  ApprovalWorkflowControllerListApprovalWorkflowsParams,
  ApprovalWorkflowItemDto,
  AWAssetDto,
  AWStageDto,
  MemberDto
} from '@api/Api';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as EmptySvg } from '@assets/icons/empty-campaigns.svg';
import CampaignsFilter from '@components/Approval/CampaignsFilter/CampaignsFilter';
import { ReactComponent as ExternalLinkSvg } from '@assets/icons/external-link.svg';
import AvatarsGroup from '@components/AvatarsGroup/AvatarsGroup';
import DatesFilter from '@components/Approval/DatesFilter/DatesFilter';
import PotentialsManagersFilter from '@components/Approval/PotentialsManagersFilter/PotentialsManagersFilter';
import StatusesFilter from '@components/Approval/StatusesFilter/StatusesFilter';

import AssetsCell from './components/AssetsCell';
import CreatedCell from './components/CreatedCell';
import ReviewMediaCell from './components/ReviewMediaCell';
import OverdueMediaCell from './components/OverdueMediaCell';
import DeadlineCell from './components/DeadlineCell';

import './ApprovalsReview.less';

type ApprovalsReviewProps = {
  approvalsList: ApprovalWorkflowItemDto[];
  approvalWorkflowsParams: Omit<
    ApprovalWorkflowControllerListApprovalWorkflowsParams,
    'workspaceId'
  >;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPageApprovals: () => void;
  hasNextPageApprovals: boolean;
};

export default memo(function ApprovalsReview({
  approvalsList,
  approvalWorkflowsParams,
  isLoading,
  isFetchingNextPage,
  fetchNextPageApprovals,
  hasNextPageApprovals
}: ApprovalsReviewProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortByParam = (searchParams.get('sortBy') ||
    'default') as ApprovalWorkflowControllerListApprovalWorkflowsParams['orderBy'];
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();

  const hasActiveFilter = Object.values(approvalWorkflowsParams || {}).some(
    (i) => !!i
  );

  const columns: ColumnsType<ApprovalWorkflowItemDto> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'assets',
        key: 'assets',
        width: '33%',
        render: (assets: AWAssetDto[], { name }: ApprovalWorkflowItemDto) => (
          <AssetsCell name={name} assets={assets} />
        )
      },
      {
        title: 'Review media',
        dataIndex: 'stages',
        key: 'stages',
        width: '12%',
        render: (stages: AWStageDto[], record: ApprovalWorkflowItemDto) => (
          <ReviewMediaCell
            stages={stages}
            assets={record.assets}
            currentStageId={record.currentStageId}
            isFinished={record.isFinished}
            isPaused={record.isPaused}
          />
        )
      },
      {
        title: 'Deadline',
        dataIndex: 'stages',
        key: 'stages',
        width: '17%',
        render: (stages: AWStageDto[], record: ApprovalWorkflowItemDto) => (
          <DeadlineCell
            stages={stages}
            currentStageId={record.currentStageId}
            isFinished={record.isFinished}
            isPaused={record.isPaused}
          />
        )
      },
      {
        title: 'Overdue media',
        dataIndex: 'stages',
        key: 'stages',
        width: '14%',
        render: (stages: AWStageDto[], record: ApprovalWorkflowItemDto) => (
          <OverdueMediaCell
            stages={stages}
            assets={record.assets}
            currentStageId={record.currentStageId}
            isFinished={record.isFinished}
            isPaused={record.isPaused}
          />
        )
      },
      {
        title: 'Owner',
        dataIndex: 'owner',
        key: 'owner',
        width: '8%',
        render: (owner: MemberDto | null) => (
          <AvatarsGroup
            maxCount={3}
            avatarSize={32}
            users={owner ? [owner] : []}
            membersType="member"
          />
        )
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '13%',
        render: (createdAt: string) => <CreatedCell createdAt={createdAt} />
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '4%',
        render: () => {
          return (
            <div className="action-cell">
              <ExternalLinkSvg />
            </div>
          );
        }
      }
    ],
    []
  );

  return (
    <div className="approvals-review">
      <div className="approvals-review_head">
        <div className="approvals-review_head-filter">
          <CampaignsFilter
            onChange={(statuses) => {
              searchParams.delete('campaignIds');
              if (statuses.length) {
                statuses.forEach((status) =>
                  searchParams.append('campaignIds', status)
                );
              }
              setSearchParams(searchParams);
            }}
            selectedCampaignIds={approvalWorkflowsParams.campaignId || []}
          />
          <DatesFilter
            onChange={(dates) => {
              searchParams.delete('dueDate');
              if (dates?.length) {
                dates.forEach((date) => searchParams.append('dueDate', date));
              }
              setSearchParams(searchParams);
            }}
            date={
              approvalWorkflowsParams.dueDateFrom &&
              approvalWorkflowsParams.dueDateTo
                ? [
                    approvalWorkflowsParams.dueDateFrom,
                    approvalWorkflowsParams.dueDateTo
                  ]
                : []
            }
            placeholder="Deadline"
            timeOptions={['today', 'tomorrow', 'nextWeek', 'nextMonth']}
          />
          <StatusesFilter
            onChange={(statuses) => {
              searchParams.delete('statuses');
              if (statuses.length) {
                statuses.forEach((status) =>
                  searchParams.append('statuses', status)
                );
              }
              setSearchParams(searchParams);
            }}
            selectedStatuses={approvalWorkflowsParams.statuses || []}
          />
          <PotentialsManagersFilter
            onChange={(statuses) => {
              searchParams.delete('managers');
              if (statuses.length) {
                statuses.forEach((status) =>
                  searchParams.append('managers', status)
                );
              }
              setSearchParams(searchParams);
            }}
            selectedManagerEmails={approvalWorkflowsParams.managers || []}
          />
          <DatesFilter
            onChange={(dates) => {
              searchParams.delete('createdDate');
              if (dates?.length) {
                dates.forEach((date) =>
                  searchParams.append('createdDate', date)
                );
              }
              setSearchParams(searchParams);
            }}
            date={
              approvalWorkflowsParams.createdAtFrom &&
              approvalWorkflowsParams.createdAtTo
                ? [
                    approvalWorkflowsParams.createdAtFrom,
                    approvalWorkflowsParams.createdAtTo
                  ]
                : []
            }
            placeholder="Created date"
            timeOptions={['today', 'tomorrow', 'prevWeek', 'prevMonth']}
          />
        </div>
        <div className="approvals-review_head-sort">
          <PageSortPanel
            isVisibleSortStyle={false}
            entity="approvals"
            sortMenuModal={
              <Menu
                selectedKeys={sortByParam ? [sortByParam] : []}
                style={{
                  minWidth: 260
                }}
                onClick={({ key }) => {
                  searchParams.set('sortBy', key);
                  setSearchParams(searchParams);
                }}
                items={[
                  { label: 'Default', key: 'default' },
                  { label: 'Need to review', key: 'needReview' },
                  { label: 'Already completed review', key: 'completedReview' }
                ]}
              />
            }
            searchInputComponent={
              <Input
                style={{ marginRight: 16, padding: '8px 12px' }}
                placeholder="Search"
                prefix={<SearchSvg />}
                size="large"
                defaultValue={approvalWorkflowsParams.searchQuery || ''}
                onChange={(e) => {
                  const _searchText = e.target.value.trim();
                  if (timeoutId.current) clearTimeout(timeoutId.current);
                  timeoutId.current = setTimeout(() => {
                    if (_searchText) searchParams.set('search', _searchText);
                    if (!_searchText) searchParams.delete('search');
                    setSearchParams(searchParams);
                  }, 400);
                }}
              />
            }
          />
        </div>
      </div>
      {isLoading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: 'calc(100vh - 380px)'
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {!isLoading && (
        <div className="approvals-review_infinite-scroll">
          <InfiniteScroll
            loading={isFetchingNextPage ? 'more' : false}
            next={fetchNextPageApprovals}
            hasMore={hasNextPageApprovals ?? false}
            loader={
              <>
                <Skeleton.Input />
                <Skeleton.Input />
              </>
            }
          >
            <div className="approvals-review-table-wrapper">
              <Table
                locale={{
                  emptyText: (
                    <>
                      {!approvalsList.length && !!hasActiveFilter && (
                        <div className="empty_state empty_state--search">
                          <LottieComponent
                            className="icon"
                            view="noSearchingResult"
                          />
                          <div className="main-body-text main-body-text--semibold">
                            No results found
                          </div>
                          <p className="copy main-body-text main-body-text--dark-gray">
                            It seems we can’t find any results based on your
                            search
                          </p>
                        </div>
                      )}
                      {!approvalsList.length && !hasActiveFilter && (
                        <div className="empty_state">
                          <EmptySvg className="icon" />
                          <div className="main-body-text main-body-text--semibold">
                            No approvals to review
                          </div>
                          <p className="copy main-body-text main-body-text--dark-gray">
                            Looks like there are no items waiting for your
                            feedback right now
                          </p>
                        </div>
                      )}
                    </>
                  )
                }}
                loading={isLoading}
                className="approvals-review-table"
                columns={columns}
                dataSource={approvalsList}
                onRow={(record: ApprovalWorkflowItemDto) => {
                  return {
                    onClick: () => {
                      const hasOnlyOneAsset = record.assets.length === 1;
                      navigate(
                        `/approval?id=${record.id}${
                          hasOnlyOneAsset
                            ? `&assetId=${record.assets[0].id}`
                            : ''
                        }`
                      );
                    }
                  };
                }}
                rowKey={(record) => record.id}
                tableLayout="fixed"
                pagination={false}
              />
            </div>
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
});
